/**
 * Provider Calendar utilities.
 *
 * Provider availability management — sync blocked dates,
 * set recurring unavailability (weekends off, etc.)
 *
 * Blocked dates stored in user.publicData.blockedDates = [
 *   '2026-07-04', '2026-07-05', ...
 * ]
 *
 * Recurring rules: user.publicData.recurringUnavailable = {
 *   daysOfWeek: [0, 6],   — 0=Sunday, 6=Saturday
 *   timeSlots: null        — future: specific hours
 * }
 *
 * External calendar sync: user.publicData.calendarSyncUrl = 'https://...'
 * (iCal/webcal URL — fetch and parse to extract VEVENT blocks)
 */

/**
 * Check if a date is blocked.
 * @param {Date|string} date
 * @param {string[]} blockedDates — YYYY-MM-DD strings
 * @param {Object} [recurringRules]
 * @returns {boolean}
 */
export const isDateBlocked = (date, blockedDates = [], recurringRules = {}) => {
  const d = new Date(date);
  const dateStr = d.toISOString().slice(0, 10);
  if (blockedDates.includes(dateStr)) return true;
  if (recurringRules.daysOfWeek?.includes(d.getDay())) return true;
  return false;
};

/**
 * Get unavailable dates in a date range.
 * @param {Date} start
 * @param {Date} end
 * @param {string[]} blockedDates
 * @param {Object} recurringRules
 * @returns {string[]} — YYYY-MM-DD strings
 */
export const getUnavailableDates = (start, end, blockedDates = [], recurringRules = {}) => {
  const unavailable = [];
  let current = new Date(start);
  current.setUTCHours(0, 0, 0, 0);
  const endDate = new Date(end);
  endDate.setUTCHours(0, 0, 0, 0);

  while (current <= endDate) {
    const dateStr = current.toISOString().slice(0, 10);
    if (isDateBlocked(current, blockedDates, recurringRules)) {
      unavailable.push(dateStr);
    }
    current = new Date(current.getTime() + 86400000);
  }
  return unavailable;
};

/**
 * Validate a date string (YYYY-MM-DD format).
 * @param {string} dateStr
 * @returns {boolean}
 */
export const isValidDateString = (dateStr) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;
  const d = new Date(dateStr);
  return !isNaN(d.getTime());
};

/**
 * Merge two blocked date arrays (deduplicated).
 * @param {string[]} a
 * @param {string[]} b
 * @returns {string[]}
 */
export const mergeBlockedDates = (a = [], b = []) =>
  [...new Set([...a, ...b])].sort();
